/**
 * PaymentOptions — COD + Razorpay (combined)
 * - No external image icons — uses emoji + Ant icons (no broken images)
 * - COD → bottom sheet confirmation before placing order
 * - X close button → exit confirmation modal
 */

import React, { useEffect, useState, useCallback } from "react";
import {
  RightOutlined,
  ExclamationCircleOutlined,
  LockOutlined,
  CreditCardOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import {
  App, Button, Card, Col, Divider,
  Drawer, Modal, Row, Skeleton, Space, theme,
} from "antd";
import { paymentAPI } from "../../api/services";

// ─── Constants ────────────────────────────────────────────────────────────────
const PAYMENT_STATUS = { PENDING: "pending", SUCCESS: "success", FAILED: "failed" };

// ─────────────────────────────────────────────────────────────────────────────
// COD Confirmation Bottom Sheet
// ─────────────────────────────────────────────────────────────────────────────
const CODDrawer = ({ open, onConfirm, onCancel, amount, currency, address, loading }) => {
  const { token } = theme.useToken();
  const display   = `${currency} ${parseFloat(amount || 0).toFixed(2)}`;

  return (
    <Drawer
      open={open}
      onClose={onCancel}
      placement="bottom"
      height="auto"
      closable={false}
      styles={{
        body:    { padding: 0 },
        wrapper: { borderRadius: "20px 20px 0 0", overflow: "hidden", boxShadow: "0 -4px 24px rgba(0,0,0,0.10)" },
      }}
    >
      {/* Pull handle */}
      <div style={{ display: "flex", justifyContent: "center", padding: "14px 0 8px" }}>
        <div style={{ width: 36, height: 4, borderRadius: 2, background: "#e0e0e0" }} />
      </div>

      <div style={{ padding: "4px 20px 36px" }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div style={{ fontSize: 28, marginBottom: 6 }}>💵</div>
          <div style={{ fontSize: 17, fontWeight: 700, color: "#111" }}>
            Confirm Cash on Delivery
          </div>
          <div style={{ fontSize: 13, color: "#999", marginTop: 3 }}>
            Review your order before placing
          </div>
        </div>

        {/* Summary box */}
        <div style={{
          background: "#f9f9f9", borderRadius: 12,
          padding: "14px 16px", marginBottom: 18,
          border: "1px solid #f0f0f0",
        }}>
          {[
            ["Payment method", "💵  Cash on Delivery"],
            ["Deliver to",     address?.full_name || "—"],
            ["Address",        `${address?.address_line1 || ""}, ${address?.city || ""}`],
          ].map(([label, value]) => (
            <Row key={label} justify="space-between" style={{ marginBottom: 10 }}>
              <span style={{ fontSize: 13, color: "#888" }}>{label}</span>
              <span style={{ fontSize: 13, fontWeight: 600, maxWidth: 170, textAlign: "right", lineHeight: 1.4 }}>
                {value}
              </span>
            </Row>
          ))}
          <Divider style={{ margin: "8px 0" }} />
          <Row justify="space-between" align="middle">
            <span style={{ fontSize: 14, fontWeight: 700 }}>Total to pay</span>
            <span style={{ fontSize: 17, fontWeight: 800, color: token.colorPrimary }}>{display}</span>
          </Row>
        </div>

        <div style={{ fontSize: 12, color: "#bbb", textAlign: "center", marginBottom: 18 }}>
          You'll pay {display} in cash when your order arrives
        </div>

        <Button
          type="primary" block size="large" loading={loading} onClick={onConfirm}
          style={{ height: 50, borderRadius: 12, fontWeight: 700, fontSize: 15, marginBottom: 10 }}
        >
          Place Order
        </Button>
        <Button
          block size="large" onClick={onCancel}
          style={{ height: 44, borderRadius: 12, fontSize: 14, border: "1px solid #e8e8e8", color: "#555" }}
        >
          Go Back
        </Button>
      </div>
    </Drawer>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Exit Confirmation Modal
// ─────────────────────────────────────────────────────────────────────────────
const ExitModal = ({ open, onStay, onExit }) => (
  <Modal
    open={open} footer={null} closable={false} centered width={300}
    styles={{ body: { padding: "28px 20px 20px", textAlign: "center" } }}
  >
    <ExclamationCircleOutlined style={{ fontSize: 40, color: "#faad14", marginBottom: 12 }} />
    <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 8, color: "#111" }}>
      Leave checkout?
    </div>
    <div style={{ fontSize: 13, color: "#888", marginBottom: 22, lineHeight: 1.6 }}>
      Your cart is saved, but your order hasn't been placed yet.
    </div>
    <Button
      type="primary" block size="large" onClick={onStay}
      style={{ borderRadius: 10, fontWeight: 600, marginBottom: 10 }}
    >
      Continue Checkout
    </Button>
    <Button
      block size="large" danger onClick={onExit}
      style={{ borderRadius: 10 }}
    >
      Exit Anyway
    </Button>
  </Modal>
);

// ─────────────────────────────────────────────────────────────────────────────
// Main PaymentOptions
// ─────────────────────────────────────────────────────────────────────────────
const PaymentOptions = ({ fetchCart, cartData, address, setPaymentStatus, setAppLoader }) => {
  const { notification }        = App.useApp();
  const { token }               = theme.useToken();
  const store                   = new URLSearchParams(window.location.search).get("store");

  const [loading, setLoading]               = useState(true);
  const [processingMethod, setProcessingMethod] = useState(null);
  const [showCOD, setShowCOD]               = useState(false);
  const [showExit, setShowExit]             = useState(false);

  const amount   = cartData?.total_amount?.amount   || 0;
  const currency = cartData?.total_amount?.currency || "INR";
  const display  = `${currency} ${parseFloat(amount).toFixed(2)}`;

  // ─── Init ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchCart?.()
      .catch(() => {})
      .finally(() => { setAppLoader?.(false); setLoading(false); });

    // Listen for X button click dispatched from App.jsx
    const onClose = () => setShowExit(true);
    window.addEventListener("checkout_close_request", onClose);
    return () => window.removeEventListener("checkout_close_request", onClose);
  }, []);

  // ─── COD ──────────────────────────────────────────────────────────────────
  const handleCODConfirm = useCallback(async () => {
    setProcessingMethod("cod");
    setPaymentStatus({ status: PAYMENT_STATUS.PENDING, method: "cod" });
    try {
      const res = await paymentAPI.createOrder({
        cart_id:      cartData?.cart_id || cartData?.uuid,
        address_id:   address?.uuid,
        payment_mode: "cod",
      });
      postToParent("CHECKOUT_FINISH", res?.data);
      setPaymentStatus({ status: PAYMENT_STATUS.SUCCESS, ...(res?.data || {}) });
    } catch (err) {
      notification.error({ message: err?.message || "Order failed. Please try again." });
      setPaymentStatus(null);
    } finally {
      setProcessingMethod(null);
      setShowCOD(false);
    }
  }, [cartData, address, notification, setPaymentStatus]);

  // ─── Razorpay ─────────────────────────────────────────────────────────────
  const handleRazorpay = useCallback(async () => {
    if (processingMethod) return;
    setProcessingMethod("online");
    setPaymentStatus({ status: PAYMENT_STATUS.PENDING, method: "online" });
    try {
      const res  = await paymentAPI.initiate({ amount: Number(amount), method: "upi" });
      const { id, amount: oAmt, currency: oCurr } = res?.data?.order || {};
      const txnId = res?.data?.txn_id;

      const rzp = new window.Razorpay({
        key:         process.env.REACT_APP_RAZORPAY_API_KEY,
        order_id:    id,
        amount:      oAmt,
        currency:    oCurr,
        name:        store || "",
        description: `Order #${id}`,
        prefill:     { name: address?.full_name, email: address?.email, contact: address?.phone },
        theme:       { color: token.colorPrimary },

        handler: async (response) => {
          try {
            const verify = await paymentAPI.verify({
              razorpay_order_id:   response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature:  response.razorpay_signature,
              txn_id:     txnId,
              cart_id:    cartData?.cart_id || cartData?.uuid,
              address_id: address?.uuid,
            });
            postToParent("CHECKOUT_FINISH", verify?.data || response);
            setPaymentStatus({ status: PAYMENT_STATUS.SUCCESS, ...(verify?.data || {}) });
          } catch {
            notification.error({ message: "Payment verification failed. Contact support." });
            setPaymentStatus({ status: PAYMENT_STATUS.FAILED });
          }
        },

        modal: {
          ondismiss: () => { setProcessingMethod(null); setPaymentStatus(null); },
        },
      });

      rzp.open();
      rzp.on("payment.failed", () => {
        setProcessingMethod(null);
        setPaymentStatus({ status: PAYMENT_STATUS.FAILED });
      });

    } catch (err) {
      notification.error({ message: err?.message || "Payment failed. Please try again." });
      setPaymentStatus(null);
      setProcessingMethod(null);
    }
  }, [amount, cartData, address, store, token, processingMethod, notification, setPaymentStatus]);

  // ─── Exit ─────────────────────────────────────────────────────────────────
  const handleExit = () => {
    postToParent("CHECKOUT_CLOSE", {});
    setShowExit(false);
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  if (loading) return <Skeleton active title={false} paragraph={{ rows: 3 }} />;

  if (!amount) return (
    <div style={{ color: "#bbb", fontSize: 13, textAlign: "center", padding: "16px 0" }}>
      Cart total unavailable. Please refresh.
    </div>
  );

  return (
    <>
      <Space direction="vertical" style={{ width: "100%" }} size={10}>

        {/* ── COD card ──────────────────────────────────────────────────────── */}
        <Card
          size="small"
          hoverable
          style={cardStyle}
          styles={{ body: { padding: "14px 16px" } }}
          onClick={() => !processingMethod && setShowCOD(true)}
        >
          <Row justify="space-between" align="middle" wrap={false}>
            <Col>
              <Row align="middle" gutter={12} wrap={false}>
                <Col>
                  <div style={iconWrap("#e8f5e9")}>
                    <span style={{ fontSize: 22, lineHeight: 1 }}>💵</span>
                  </div>
                </Col>
                <Col>
                  <div style={{ fontWeight: 700, fontSize: 14, color: "#111" }}>
                    Cash on Delivery
                  </div>
                  <div style={{ fontSize: 11, color: "#aaa", marginTop: 2 }}>
                    Pay when your order arrives
                  </div>
                </Col>
              </Row>
            </Col>
            <Col>
              <Row align="middle" gutter={6} wrap={false}>
                <Col><span style={{ fontWeight: 700, fontSize: 14 }}>{display}</span></Col>
                <Col><RightOutlined style={{ fontSize: 11, color: "#ccc" }} /></Col>
              </Row>
            </Col>
          </Row>
        </Card>

        {/* ── Razorpay card ──────────────────────────────────────────────────── */}
        <Card
          size="small"
          hoverable
          loading={processingMethod === "online"}
          style={{ ...cardStyle, opacity: processingMethod === "online" ? 0.7 : 1 }}
          styles={{ body: { padding: "14px 16px" } }}
          onClick={handleRazorpay}
        >
          <Row justify="space-between" align="middle" wrap={false}>
            <Col flex={1}>
              <Row align="middle" gutter={12} wrap={false}>
                <Col>
                  <div style={iconWrap("#e8f0fe")}>
                    <LockOutlined style={{ fontSize: 18, color: "#1a73e8" }} />
                  </div>
                </Col>
                <Col flex={1}>
                  <Row align="middle" gutter={6}>
                    <Col>
                      <span style={{ fontWeight: 700, fontSize: 14, color: "#111" }}>
                        Secure Online Payment
                      </span>
                    </Col>
                    <Col>
                      <div style={{
                        fontSize: 9, fontWeight: 700, padding: "1px 6px",
                        borderRadius: 4, backgroundColor: "#e8f5e9",
                        color: "#2e7d32", border: "1px solid #c8e6c9",
                      }}>
                        RECOMMENDED
                      </div>
                    </Col>
                  </Row>
                  <div style={{ fontSize: 11, color: "#aaa", marginTop: 2 }}>
                    via Razorpay &nbsp;·&nbsp; UPI, Cards, Wallets, Netbanking
                  </div>
                  {/* Method badges */}
                  <Row gutter={4} style={{ marginTop: 8 }}>
                    {[
                      { label: "UPI",     bg: "#fff3e0", color: "#e65100" },
                      { label: "VISA",    bg: "#e3f2fd", color: "#0d47a1" },
                      { label: "MC",      bg: "#fce4ec", color: "#880e4f" },
                      { label: "RuPay",   bg: "#f3e5f5", color: "#4a148c" },
                      { label: "Wallet",  bg: "#e8f5e9", color: "#1b5e20" },
                    ].map(({ label, bg, color }) => (
                      <Col key={label}>
                        <div style={{ ...methodBadge, backgroundColor: bg, color, borderColor: `${color}33` }}>
                          {label}
                        </div>
                      </Col>
                    ))}
                  </Row>
                </Col>
              </Row>
            </Col>
            <Col style={{ paddingLeft: 8 }}>
              <Row align="middle" gutter={6} wrap={false}>
                <Col><span style={{ fontWeight: 700, fontSize: 14 }}>{display}</span></Col>
                <Col><RightOutlined style={{ fontSize: 11, color: "#ccc" }} /></Col>
              </Row>
            </Col>
          </Row>
        </Card>

        {/* Secure note */}
        <Row justify="center" style={{ marginTop: 4 }}>
          <Space size={4}>
            <LockOutlined style={{ fontSize: 11, color: "#ccc" }} />
            <span style={{ fontSize: 11, color: "#ccc" }}>
              256-bit encrypted &amp; secure payments
            </span>
          </Space>
        </Row>

      </Space>

      {/* COD Bottom Sheet */}
      <CODDrawer
        open={showCOD}
        onConfirm={handleCODConfirm}
        onCancel={() => setShowCOD(false)}
        amount={amount}
        currency={currency}
        address={address}
        loading={processingMethod === "cod"}
      />

      {/* Exit Confirmation */}
      <ExitModal
        open={showExit}
        onStay={() => setShowExit(false)}
        onExit={handleExit}
      />
    </>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const cardStyle = {
  borderRadius: 12,
  border: "1px solid #ebebeb",
  boxShadow: "0 1px 6px rgba(0,0,0,0.05)",
  cursor: "pointer",
  transition: "box-shadow 0.2s, transform 0.1s",
};

const iconWrap = (bg) => ({
  width: 42, height: 42, borderRadius: 10,
  backgroundColor: bg,
  display: "flex", alignItems: "center", justifyContent: "center",
  flexShrink: 0,
});

const methodBadge = {
  fontSize: 9, fontWeight: 700,
  padding: "2px 5px", borderRadius: 4,
  border: "1px solid",
  letterSpacing: "0.3px",
};

const postToParent = (type, payload) => {
  try { window.parent.postMessage({ type, payload }, "*"); } catch {}
};

export default PaymentOptions;